/**
 * Hook para gestionar documentos legales
 * Proporciona funciones CRUD y queries con React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type {
  LegalDocument,
  LegalDocumentCategory,
  LegalDocumentInput,
  LegalDocumentAuditLog,
  DocumentStatus,
  DocumentType,
} from '@/schemas/legalDocumentSchemas';

const QUERY_KEY = 'legal-documents';
const CATEGORIES_KEY = 'legal-document-categories';

interface UseDocumentsListOptions {
  categoryId?: string | null;
  folderId?: string | null;
  status?: DocumentStatus | 'all';
  documentType?: DocumentType | 'all';
  search?: string;
  expiringInDays?: number;
}

/**
 * Hook para listar documentos legales
 */
export function useLegalDocumentsList(options: UseDocumentsListOptions = {}) {
  const { categoryId, folderId, status, documentType, search, expiringInDays } = options;

  return useQuery({
    queryKey: [QUERY_KEY, 'list', categoryId, folderId, status, documentType, search, expiringInDays],
    queryFn: async () => {
      let query = api
        .from('legal_documents')
        .select('*, category:legal_document_categories(*), folder:document_folders(id, name, color)')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (folderId) {
        query = query.eq('folder_id', folderId);
      } else if (folderId === null && options.hasOwnProperty('folderId')) {
        query = query.is('folder_id', null);
      }

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      if (documentType && documentType !== 'all') {
        query = query.eq('document_type', documentType);
      }

      if (search) {
        query = query.ilike('title', `%${search}%`);
      }

      if (expiringInDays) {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + expiringInDays);
        query = query.lte('expiration_date', futureDate.toISOString().split('T')[0]);
        query = query.gte('expiration_date', new Date().toISOString().split('T')[0]);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as LegalDocument[];
    },
  });
}

/**
 * Hook para obtener un documento por ID
 */
export function useLegalDocumentById(id: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await api
        .from('legal_documents')
        .select('*, category:legal_document_categories(*)')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as LegalDocument;
    },
    enabled: !!id,
  });
}

/**
 * Hook para listar categorías
 */
export function useLegalDocumentCategories() {
  return useQuery({
    queryKey: [CATEGORIES_KEY],
    queryFn: async () => {
      const { data, error } = await api
        .from('legal_document_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []) as LegalDocumentCategory[];
    },
  });
}

/**
 * Hook para crear un documento
 */
export function useCreateLegalDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: LegalDocumentInput & { created_by?: string; folder_id?: string | null }) => {
      const { error, data: result } = await api
        .from('legal_documents')
        .insert({
          title: data.title,
          code: data.code || null,
          description: data.description || null,
          category_id: data.category_id || null,
          folder_id: data.folder_id || null,
          document_type: data.document_type,
          file_url: data.file_url || null,
          file_name: data.file_name || null,
          file_size: data.file_size || null,
          file_type: data.file_type || null,
          version: data.version || '1.0',
          effective_date: data.effective_date || null,
          expiration_date: data.expiration_date || null,
          status: data.status || 'draft',
          issued_by: data.issued_by || null,
          review_date: data.review_date || null,
          tags: data.tags || [],
          keywords: data.keywords || null,
          confidentiality_level: data.confidentiality_level || 'internal',
          notes: data.notes || null,
          created_by: data.created_by,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Documento creado',
        description: 'El documento se ha guardado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear el documento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para actualizar un documento
 */
export function useUpdateLegalDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LegalDocumentInput> }) => {
      const { error } = await api
        .from('legal_documents')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Documento actualizado',
        description: 'Los cambios se han guardado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar el documento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para aprobar un documento
 */
export function useApproveLegalDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, approvedBy }: { id: string; approvedBy: string }) => {
      const { error } = await api
        .from('legal_documents')
        .update({
          status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Documento aprobado',
        description: 'El documento ha sido aprobado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo aprobar el documento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para archivar un documento
 */
export function useArchiveLegalDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('legal_documents')
        .update({
          status: 'archived',
          is_active: false,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Documento archivado',
        description: 'El documento se ha archivado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo archivar el documento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para eliminar un documento (soft delete)
 */
export function useDeleteLegalDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('legal_documents')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Documento eliminado',
        description: 'El documento se ha eliminado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el documento',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para obtener historial de auditoría de un documento
 */
export function useLegalDocumentAuditLog(documentId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, 'audit', documentId],
    queryFn: async () => {
      if (!documentId) return [];

      const { data, error } = await api
        .from('legal_document_audit_log')
        .select('*')
        .eq('document_id', documentId)
        .order('performed_at', { ascending: false });

      if (error) throw error;
      return (data || []) as LegalDocumentAuditLog[];
    },
    enabled: !!documentId,
  });
}

/**
 * Hook para registrar una acción en el log de auditoría
 */
export function useLogDocumentAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      action,
      performedBy,
      details,
    }: {
      documentId: string;
      action: string;
      performedBy?: string;
      details?: Record<string, any>;
    }) => {
      const { error } = await api.from('legal_document_audit_log').insert({
        document_id: documentId,
        action,
        action_details: details || null,
        performed_by: performedBy || null,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'audit', variables.documentId] });
    },
  });
}

/**
 * Hook para obtener estadísticas de documentos
 */
export function useLegalDocumentsStats() {
  const { data: documents = [] } = useLegalDocumentsList();

  const now = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(now.getDate() + 30);

  const stats = {
    total: documents.length,
    active: documents.filter((d) => d.status === 'active').length,
    draft: documents.filter((d) => d.status === 'draft').length,
    pendingApproval: documents.filter((d) => d.status === 'pending_approval').length,
    expired: documents.filter((d) => d.status === 'expired').length,
    expiringInThirtyDays: documents.filter((d) => {
      if (!d.expiration_date) return false;
      const expDate = new Date(d.expiration_date);
      return expDate >= now && expDate <= thirtyDaysFromNow;
    }).length,
    byType: documentTypes.reduce(
      (acc, type) => {
        acc[type] = documents.filter((d) => d.document_type === type).length;
        return acc;
      },
      {} as Record<string, number>
    ),
  };

  return stats;
}

// Importar tipos para el reduce
import { documentTypes } from '@/schemas/legalDocumentSchemas';
